import React, { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Image } from "@/components/common/image";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { CloseIcon, DownloadIcon, MoreIcon, TrashIcon } from "@/components/ui/icon";
import { ModalCloseButton } from "@/components/common/modal-close-button";
import { useDownload } from "@/hooks/use-download";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { AgentShareModal } from "../agent-share-modal";
import { PublishIcon } from "@/components/agent-share-modal/assets";
import { TextButton } from "@/components/common/text-button";
import { IconButton } from "@/components/common/icon-button";
import { MoreActionItem, MoreActionPopover } from "@/components/common/more-action-popover";
import { Edit3, RefreshCcw } from "lucide-react";
import { ConfirmModal } from "@/components/common/confirm-modal";
import { AccountCard } from "../common/account-card";
import { CopyClipboard } from "../common/copy-clipboard";

export interface AgentPostModalProps {
  open: boolean;
  // 右侧信息
  avatarUrl: string;
  nickname: string;
  title: string;
  text: string;
  images: string[];
  deleteLoading?: boolean;
  isAutoPush?: boolean;
  onOpenChange: (open: boolean) => void;
  onCopyText?: (text: string) => void;
  onDownloadAll?: (images: string[]) => void;
  onReedit?: () => void;
  onRegenerate?: () => void;
  onDelete?: () => Promise<void>;
}

const CopySvg = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

const ShareSvg = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="18" cy="5" r="3"></circle>
    <circle cx="6" cy="12" r="3"></circle>
    <circle cx="18" cy="19" r="3"></circle>
    <path d="M8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49"></path>
  </svg>
);

export const AgentPostModal: React.FC<AgentPostModalProps> = ({
  open,
  avatarUrl,
  nickname,
  title,
  text,
  images,
  deleteLoading,
  isAutoPush,
  onOpenChange,
  onCopyText,
  onDownloadAll,
  onReedit,
  onRegenerate,
  onDelete,
}) => {
  const { t } = useTranslation();
  const imageRef = useRef<HTMLImageElement>(null);
  const { download, downloadZip } = useDownload();
  const { toast } = useToast();

  const handleCopy = async () => {
    try {
      if (onCopyText) {
        await Promise.resolve(onCopyText(text));
        toast.default({ description: t('agent_creation.agent_modal.copy_text') });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast.default({ description: t('agent_creation.agent_modal.copied') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.copy_fail') });
    }
  };

  const handleDownloadAll = async () => {
    try {
      const list = images.slice(0, 9).map((url, i) => ({ url, filename: `image_${i + 1}.jpg` }));
      if (onDownloadAll) {
        await Promise.resolve(onDownloadAll(images.slice(0, 9)));
        toast.default({ description: t('agent_creation.agent_modal.download_complete') });
        return;
      }
      await downloadZip(list, "images.zip");
      toast.default({ description: t('agent_creation.agent_modal.download_complete') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.download_fail') });
    }
  };
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft")
        setSelectedIndex((i) => (i - 1 + images.length) % images.length);
      if (e.key === "ArrowRight")
        setSelectedIndex((i) => (i + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, images.length]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [open]);

  // 新增：下载当前单张图片
  const handleDownloadCurrent = async () => {
    const url = images[selectedIndex];
    if (!url) return;
    try {
      await download(url, `image_${selectedIndex + 1}.jpg`);
      toast.default({ description: t('agent_creation.agent_modal.download_complete') });
    } catch {
      toast.error({ description: t('agent_creation.agent_modal.download_fail') });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[24px] shadow-lg z-50 flex h-[90vh]">
          <div className="group relative h-full aspect-[3/4] bg-black flex items-center justify-center shrink-0 grow-0">
            {/* 叠加：当前/总数 */}
            <div className="absolute top-3 right-3 px-2 py-1 rounded bg-black/60 text-white text-sm z-10">
              {selectedIndex + 1}/{images.length}
            </div>

            {/* 叠加：左右轮播按钮（透明黑底白色图标） */}
            <button
              onClick={() =>
                setSelectedIndex((i) => (i - 1 + images.length) % images.length)
              }
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded bg-black/40 hover:bg-black/60 text-white flex items-center justify-center z-10 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Previous"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button
              onClick={() => setSelectedIndex((i) => (i + 1) % images.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded bg-black/40 hover:bg-black/60 text-white flex items-center justify-center z-10 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Next"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>

            {/* 叠加：右下角下载单张图片 */}
            <button
              onClick={handleDownloadCurrent}
              className="absolute bottom-4 right-4 px-3 py-2 rounded-md bg-black/60 hover:bg-black/70 text-white flex items-center gap-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <DownloadIcon className="w-5 h-5" />
              <span className="text-sm">{t('agent_creation.agent_modal.download_single_image')}</span>
            </button>

            <div className="relative w-full h-full flex items-center justify-center">
              <Image
                ref={imageRef}
                src={images[selectedIndex]}
                className="h-full w-auto object-contain"
                alt="moment-cover"
                crossOrigin="anonymous"
              />
            </div>
          </div>

          {/* 右侧：白底信息区（固定 320px） */}
          <div className="w-[400px] h-full bg-white text-[#111827] flex flex-col shrink-0 grow-0">
            {/* 头像 + 昵称 */}
            <div className="flex justify-between items-center p-5">
              <div className="flex items-center gap-3">
                <AccountCard
                  compact
                  className="size-10"
                  name={nickname}
                  avatar={avatarUrl}
                />
                <div className="text-lg font-medium">{nickname}</div>
              </div>
              <ModalCloseButton onClick={() => onOpenChange(false)} />
            </div>

            {/* 文案 */}
            <div className="flex-1 min-h-0 overflow-auto p-4 pt-0 text-sm leading-5 text-[#333333] font-normal whitespace-pre-line">
              <div className="pb-3 leading-[26px] font-medium text-[rgba(16,24,40,1)] text-[17px]">
                {title}
              </div>
              {text}
            </div>

            <div className="mt-auto  flex justify-end items-center gap-3 p-5">
              <MoreActionPopover
                className="w-40"
                side="top"
                align="center"
                tiggerClassName="size-9 rounded-[8px]"
                tiggerIcon={
                  <IconButton
                    icon={<MoreIcon className="size-5 !text-[rgba(51,51,51,1)]" />}
                  />
                }
              >
                {!isAutoPush && (
                  <>
                    <MoreActionItem
                      icon={<Edit3 className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                      className='text-[rgba(16,24,40,1)]'
                      label={t("agent_creation.content.reedit")}
                      onClick={() => {
                        onReedit?.();
                        onOpenChange(false);
                      }}
                    />
                    {/* <MoreActionItem
                      icon={<RefreshCcw className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                      className='text-[rgba(16,24,40,1)]'
                      label={t("agent_creation.content.regenerate_again")}
                      onClick={() => {
                        onRegenerate?.();
                        onOpenChange(false);
                      }}
                    /> */}
                  </>
                )}
                <ConfirmModal
                  trigger={
                    <MoreActionItem
                      icon={<TrashIcon className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                      className='text-[rgba(16,24,40,1)]'
                      label={t("common.delete")}
                    />
                  }
                  title={t('agent_creation.content.delete_title')}
                  description={t('agent_creation.content.delete_desc')}
                  cancel={t('common.cancel')}
                  confirm={t('common.delete')}
                  onConfirm={async () => {
                    await onDelete?.();
                    onOpenChange(false);
                  }}
                  loading={deleteLoading}
                />
              </MoreActionPopover>
              <CopyClipboard
                className="size-9 rounded-[8px]"
                iconClassName="w-5 h-5 text-[rgba(51,51,51,1)]"
                text={text}
                icon={<CopySvg className="w-5 h-5 text-[rgba(51,51,51,1)]" />}
              />
              <IconButton
                className="size-9 rounded-[8px]"
                icon={<DownloadIcon className="w-5 h-5 text-[rgba(51,51,51,1)]" />}
                onClick={handleDownloadAll}
              />
              <AgentShareModal
                showDouyin
                douyinOptions={{
                  imageUrls: images
                }}
                showWeChatOfficialAccount
                showXiaoHongShu
                trigger={
                  <TextButton
                    className="px-[22px] py-[10px] rounded-[8px] max-w-max text-sm max-h-max"
                    icon={<PublishIcon className="w-5 h-5" />}
                    label={t('agent_creation.agent_modal.publish')}
                  />
                }
              />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AgentPostModal;
